import React, { useEffect, useMemo, useState, useRef, forwardRef, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Database, Info, MoreVertical, Settings2, Trash2, ChevronDown, Loader2, CloudOff, Lightbulb, X, Sparkles, Copy, Cpu, Lock, SlidersHorizontal } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { RegionSelector } from './RegionSelector';
import { useWeatherForecast, getVariableUnit, getVariableValue } from '../../hooks/useWeatherData';
import { useENTSOEData, isENTSOEVariable } from '../../hooks/useENTSOE';
import { ChartAiEditPanel } from './ChartAiEditPanel';
import { useAthena } from '../../hooks/useAthena';
import { ChartSettingsPanel, defaultSettings, ChartSettings } from './ChartSettingsPanel';
import { TableModelDropdown } from './TableModelDropdown';
import type { ModelOverride } from './ModelRunSelector';
import type { LocationOverride } from './LocationSelector';
// Seeded random for model variation offsets
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}
/** A single model+run entry */
interface ChartEntry {
  id: string;
  modelName: string;
  runIso: string;
}
function makeEntryId(modelName: string, runIso: string): string {
  return runIso ? `${modelName}__${runIso}` : modelName;
}
function formatRunShort(runIso: string): string {
  if (!runIso) return '';
  const d = new Date(runIso);
  return d.getUTCHours().toString().padStart(2, '0') + 'z';
}
const regions = ['UK Wind 100m', 'Europe Wind 100m', 'US Wind 100m', 'Nordic Wind 100m'];
const timePeriods = ['1 Day', '3 Days', '5 Days', '7 Days', '20 Days', '60 Days'];
interface ModelOption {
  name: string;
  category: 'ai' | 'nwp' | 'live';
  liveOnly?: boolean;
  hourlyRuns?: boolean;
}
const ALL_MODELS: ModelOption[] = [{
  name: 'EC AIFS',
  category: 'ai'
}, {
  name: 'EPT-2',
  category: 'ai'
}, {
  name: 'EPT-2 Early',
  category: 'ai'
}, {
  name: 'EPT-2 RR',
  category: 'ai'
}, {
  name: 'EPT-2 HRRR',
  category: 'ai',
  hourlyRuns: true
}, {
  name: 'EPT-2e',
  category: 'ai'
}, {
  name: 'Microsoft Aurora',
  category: 'ai'
}, {
  name: 'EC ENS',
  category: 'ai'
}, {
  name: 'EC IFS',
  category: 'nwp'
}, {
  name: 'GFS',
  category: 'nwp'
}, {
  name: 'ICON Global',
  category: 'nwp'
}, {
  name: 'ICON-EU',
  category: 'nwp'
}, {
  name: 'EC Ensemble Mean',
  category: 'live',
  liveOnly: true
}, {
  name: 'GFS Ensemble Mean',
  category: 'live',
  liveOnly: true
}, {
  name: 'UKMO Global (10km)',
  category: 'live',
  liveOnly: true
}];
const MODEL_HEX_COLORS: Record<string, string> = {
  'EPT-2': '#EC4899',
  'EC AIFS': '#3b82f6',
  'EC IFS': '#06b6d4',
  'EC-IFS': '#06b6d4',
  'EPT-2 Early': '#14b8a6',
  'EPT-2 RR': '#10b981',
  'EPT-2e': '#8b5cf6',
  'EPT-2 HRRR': '#f43f5e',
  'Microsoft Aurora': '#f59e0b',
  'EC ENS': '#f97316',
  GFS: '#eab308',
  'ICON Global': '#a855f7',
  'ICON-EU': '#c084fc',
  'EC Ensemble Mean': '#64748b',
  'GFS Ensemble Mean': '#94a3b8',
  'UKMO Global (10km)': '#fb7185'
};
const FALLBACK_COLORS = ['#06b6d4', '#f59e0b', '#a855f7', '#f97316', '#64748b', '#fb7185'];
function getModelHexColor(name: string, index: number): string {
  return MODEL_HEX_COLORS[name] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}
interface ChartWidgetProps {
  id: string;
  onRemove?: () => void;
  onDuplicate?: () => void;
  onParameterChange?: () => void;
  onDoubleClickHandle?: () => void;
  modelOverride?: ModelOverride | null;
  locationOverride?: LocationOverride | null;
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
}
export const ChartWidget = forwardRef<HTMLDivElement, ChartWidgetProps>(({
  id,
  onRemove,
  onDuplicate,
  onParameterChange,
  onDoubleClickHandle,
  modelOverride,
  locationOverride,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  ...props
}, ref) => {
  const [region, setRegion] = useState('Germany');
  const [timePeriod, setTimePeriod] = useState('7 Days');
  const [variable, setVariable] = useState('Wind Speed 100m');
  const [variableOpen, setVariableOpen] = useState(false);
  const variableButtonRef = useRef<HTMLButtonElement>(null);
  // Settings state
  const [settings, setSettings] = useState<ChartSettings>(defaultSettings);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // AI edit panel state
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const {
    generateText
  } = useAthena();
  const handleAiEdit = async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    setAiResult('');
    const lower = aiPrompt.toLowerCase();
    // --- Variable detection ---
    const VARIABLE_MAP: Record<string, string> = {
      temperature: 'Temp 2m',
      temp: 'Temp 2m',
      'dew point': 'Dew Point 2m',
      'wind speed 10': 'Wind Speed 10m',
      'wind 10': 'Wind Speed 10m',
      'wind speed 100': 'Wind Speed 100m',
      'wind 100': 'Wind Speed 100m',
      'wind speed': 'Wind Speed 100m',
      wind: 'Wind Speed 100m',
      precipitation: 'Precipitation 1h',
      rain: 'Precipitation 1h',
      solar: 'SSRD 1h',
      radiation: 'SSRD 1h',
      ssrd: 'SSRD 1h',
      pressure: 'MSL Pressure',
      'cloud cover high': 'Cloud Cover High',
      'cloud cover medium': 'Cloud Cover Medium',
      'cloud cover low': 'Cloud Cover Low',
      cloud: 'Cloud Cover High',
      'day-ahead': 'Day-ahead Prices',
      'day ahead': 'Day-ahead Prices',
      'imbalance price': 'Imbalance Prices',
      'generation actual': 'Generation (Actual)',
      'generation forecast': 'Generation Forecast (Day-ahead)',
      'wind solar': 'Wind & Solar Forecast (Day-ahead)',
      'load actual': 'Load (Actual)',
      'load forecast': 'Load Forecast (Day-ahead)'
    };
    let detectedVariable: string | null = null;
    for (const [keyword, varName] of Object.entries(VARIABLE_MAP)) {
      if (lower.includes(keyword)) {
        detectedVariable = varName;
        break;
      }
    }
    // --- Region detection ---
    const REGIONS = ['Germany', 'United Kingdom', 'UK', 'France', 'Spain', 'Italy', 'Netherlands', 'Poland', 'Sweden', 'Norway', 'Nordic', 'Europe', 'Berlin', 'London', 'Paris', 'Madrid', 'Rome', 'Amsterdam', 'North Sea', 'Baltic', 'Mediterranean'];
    let detectedRegion: string | null = null;
    for (const r of REGIONS) {
      if (lower.includes(r.toLowerCase())) {
        detectedRegion = r === 'UK' ? 'United Kingdom' : r;
        break;
      }
    }
    // --- Time period detection ---
    const TIME_PERIODS = ['60 Days', '20 Days', '7 Days', '5 Days', '3 Days', '1 Day'];
    let detectedPeriod: string | null = null;
    for (const t of TIME_PERIODS) {
      if (lower.includes(t.toLowerCase()) || lower.includes(t.replace(' ', ''))) {
        detectedPeriod = t;
        break;
      }
    }
    if (!detectedPeriod) {
      if (/\bweek\b/.test(lower)) detectedPeriod = '7 Days';else if (/\bmonth\b/.test(lower)) detectedPeriod = '20 Days';else if (/\bday\b/.test(lower) && /\b1\b/.test(lower)) detectedPeriod = '1 Day';
    }
    // --- Model detection ---
    const MODEL_NAMES = ['EC AIFS', 'EPT-2 Early', 'EPT-2 RR', 'EPT-2 HRRR', 'EPT-2e', 'EPT-2', 'EC IFS', 'EC-IFS', 'GFS', 'ICON Global', 'ICON-EU', 'Microsoft Aurora', 'EC ENS'];
    const detectedModels: string[] = [];
    for (const m of MODEL_NAMES) {
      if (lower.includes(m.toLowerCase())) detectedModels.push(m);
    }
    const hasChanges = detectedVariable || detectedRegion || detectedPeriod || detectedModels.length > 0;
    // Close the modal immediately if we detected changes
    if (hasChanges) {
      setAiEditOpen(false);
      setAiResult('');
      setAiPrompt('');
    }
    // Apply data changes after a short delay so the modal can animate out first
    setTimeout(() => {
      if (detectedVariable && detectedVariable !== variable) {
        setVariable(detectedVariable);
        onParameterChange?.();
      }
      if (detectedRegion && detectedRegion !== region) {
        setRegion(detectedRegion);
        onParameterChange?.();
      }
      if (detectedPeriod && detectedPeriod !== timePeriod) {
        setTimePeriod(detectedPeriod);
        onParameterChange?.();
      }
      if (detectedModels.length > 0) {
        setEntries(detectedModels.map(name => ({
          id: name,
          modelName: name,
          runIso: ''
        })));
        setHiddenModels(new Set());
        onParameterChange?.();
      }
    }, 250);
    // Build summary for Athena
    const actions = [detectedVariable ? `switched variable to "${detectedVariable}"` : '', detectedRegion ? `changed region to ${detectedRegion}` : '', detectedPeriod ? `set time period to ${detectedPeriod}` : '', detectedModels.length > 0 ? `selected models: ${detectedModels.join(', ')}` : ''].filter(Boolean).join('; ');
    try {
      const result = await generateText(`You are Athena, the intelligent chart assistant for the Jua energy trading platform. The user is editing a line chart widget. Current settings: variable="${variable}", region="${region}", period="${timePeriod}", models=${entries.map(e => e.modelName).join(', ')}. The system has already applied these changes: ${actions || 'none detected'}. Confirm what was done in 1-2 concise sentences. If nothing was detected, suggest what the user can change.`, aiPrompt);
      // Only show result if panel is still open (no changes detected case)
      if (!hasChanges) setAiResult(result);
    } catch {
      if (!hasChanges) setAiResult('Something went wrong. Please try again.');
    } finally {
      setAiLoading(false);
      // If no changes were detected, close after showing the response
      if (!hasChanges) {
        setTimeout(() => {
          setAiEditOpen(false);
          setAiResult('');
          setAiPrompt('');
        }, 1800);
      }
    }
  };
  // Multi-run model entries
  const [entries, setEntries] = useState<ChartEntry[]>([{
    id: 'EC AIFS',
    modelName: 'EC AIFS',
    runIso: ''
  }, {
    id: 'EC IFS',
    modelName: 'EC IFS',
    runIso: ''
  }, {
    id: 'EPT-2',
    modelName: 'EPT-2',
    runIso: ''
  }]);
  // For backward compat, derive selectedModels from entries (unique entry ids used as data keys)
  const selectedModels = useMemo(() => entries.map(e => e.id), [entries]);
  // Store pre-override entries for restoration
  const preOverrideEntriesRef = useRef<ChartEntry[] | null>(null);
  // Handle model override changes
  useEffect(() => {
    if (modelOverride) {
      if (!preOverrideEntriesRef.current) {
        preOverrideEntriesRef.current = [...entries];
      }
      const overrideEntries: ChartEntry[] = modelOverride.runs.map(run => ({
        id: makeEntryId(run.modelName, new Date(run.timestamp).toISOString()),
        modelName: run.modelName,
        runIso: new Date(run.timestamp).toISOString()
      }));
      setEntries(overrideEntries);
      setHiddenModels(new Set());
    } else if (preOverrideEntriesRef.current) {
      setEntries(preOverrideEntriesRef.current);
      preOverrideEntriesRef.current = null;
      setHiddenModels(new Set());
    }
  }, [modelOverride]);
  // Handle location override changes
  const preOverrideRegionRef = useRef<string | null>(null);
  useEffect(() => {
    if (locationOverride) {
      if (!preOverrideRegionRef.current) {
        preOverrideRegionRef.current = region;
      }
      setRegion(locationOverride.regionName);
    } else if (preOverrideRegionRef.current) {
      setRegion(preOverrideRegionRef.current);
      preOverrideRegionRef.current = null;
    }
  }, [locationOverride]);
  const [modelOpen, setModelOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  // Hidden models (toggled via legend clicks)
  const [hiddenModels, setHiddenModels] = useState<Set<string>>(new Set());
  // Menu states
  const [menuOpen, setMenuOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  // Contextual tip state
  const [showTip, setShowTip] = useState(true);
  const [tipVisible, setTipVisible] = useState(false);
  // Auto-show and auto-dismiss contextual tip
  useEffect(() => {
    const fadeIn = setTimeout(() => setTipVisible(true), 800);
    const fadeOut = setTimeout(() => setTipVisible(false), 7000);
    const remove = setTimeout(() => setShowTip(false), 7600);
    return () => {
      clearTimeout(fadeIn);
      clearTimeout(fadeOut);
      clearTimeout(remove);
    };
  }, []);
  // Refs
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const regionButtonRef = useRef<HTMLButtonElement>(null);
  const timeButtonRef = useRef<HTMLButtonElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);
  const closeAllDropdowns = () => {
    setRegionOpen(false);
    setTimeOpen(false);
    setModelOpen(false);
    setMenuOpen(false);
    setModelSearch('');
    setVariableOpen(false);
  };
  const toggleModel = (name: string) => {
    setEntries(prev => {
      const hasEntries = prev.some(e => e.modelName === name);
      if (hasEntries) {
        const filtered = prev.filter(e => e.modelName !== name);
        if (filtered.length === 0) return prev; // keep at least 1
        setHiddenModels(h => {
          const next = new Set(h);
          // Remove hidden entries for this model
          prev.filter(e => e.modelName === name).forEach(e => next.delete(e.id));
          return next;
        });
        return filtered;
      }
      return [...prev, {
        id: makeEntryId(name, ''),
        modelName: name,
        runIso: ''
      }];
    });
    onParameterChange?.();
  };
  // Toggle a specific run for a model
  const handleToggleRun = (modelName: string, runValue: string) => {
    setEntries(prev => {
      const entryId = makeEntryId(modelName, runValue);
      const exists = prev.find(e => e.id === entryId);
      if (exists) {
        const filtered = prev.filter(e => e.id !== entryId);
        if (filtered.length === 0) return prev;
        setHiddenModels(h => {
          const next = new Set(h);
          next.delete(entryId);
          return next;
        });
        return filtered;
      }
      // Remove default (no-run) entry for this model, add specific run
      const withoutDefault = prev.filter(e => !(e.modelName === modelName && e.runIso === ''));
      return [...withoutDefault, {
        id: entryId,
        modelName,
        runIso: runValue
      }];
    });
    onParameterChange?.();
  };
  // Clear all models — reset to defaults
  const handleClearAll = () => {
    setEntries([{
      id: makeEntryId('EC AIFS', ''),
      modelName: 'EC AIFS',
      runIso: ''
    }, {
      id: makeEntryId('EC IFS', ''),
      modelName: 'EC IFS',
      runIso: ''
    }, {
      id: makeEntryId('EPT-2', ''),
      modelName: 'EPT-2',
      runIso: ''
    }]);
    setHiddenModels(new Set());
    onParameterChange?.();
  };
  // Available runs (6-hourly, last 2 days)
  const availableRuns = useMemo(() => {
    const runs: {
      label: string;
      value: string;
      date: Date;
    }[] = [];
    const now = new Date();
    for (let dayOffset = 0; dayOffset >= -1; dayOffset--) {
      const day = new Date(now);
      day.setUTCDate(day.getUTCDate() + dayOffset);
      for (const hour of [18, 12, 6, 0]) {
        const runDate = new Date(day);
        runDate.setUTCHours(hour, 0, 0, 0);
        if (runDate.getTime() <= now.getTime()) {
          const dayLabel = dayOffset === 0 ? 'Today' : 'Yesterday';
          runs.push({
            label: `${dayLabel}, ${hour.toString().padStart(2, '0')}:00 UTC`,
            value: runDate.toISOString(),
            date: runDate
          });
        }
      }
    }
    return runs;
  }, []);
  // Hourly runs for HRRR-type models (last 24 hours)
  const availableHourlyRuns = useMemo(() => {
    const runs: {
      label: string;
      value: string;
      date: Date;
    }[] = [];
    const now = new Date();
    for (let hoursAgo = 0; hoursAgo < 24; hoursAgo++) {
      const runDate = new Date(now);
      runDate.setUTCHours(runDate.getUTCHours() - hoursAgo, 0, 0, 0);
      if (runDate.getTime() <= now.getTime()) {
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setUTCDate(yesterday.getUTCDate() - 1);
        let dayLabel: string;
        if (runDate >= today) {
          dayLabel = 'Today';
        } else if (runDate >= yesterday) {
          dayLabel = 'Yesterday';
        } else {
          dayLabel = runDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
          });
        }
        const hour = runDate.getUTCHours();
        runs.push({
          label: `${dayLabel}, ${hour.toString().padStart(2, '0')}:00 UTC`,
          value: runDate.toISOString(),
          date: runDate
        });
      }
    }
    return runs;
  }, []);
  // Convert entries to map format for the dropdown
  const selectedModelRuns = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const e of entries) {
      if (!map[e.modelName]) map[e.modelName] = [];
      map[e.modelName].push(e.runIso);
    }
    return map;
  }, [entries]);
  // Fetch real weather data
  const {
    data: forecastData,
    loading,
    error,
    cityName
  } = useWeatherForecast(region);
  // Fetch ENTSOE data when an ENTSOE variable is selected
  const daysMatch = timePeriod.match(/(\d+)/);
  const requestedDays = daysMatch ? parseInt(daysMatch[1]) : 7;
  const {
    data: entsoeData,
    loading: entsoeLoading,
    error: entsoeError,
    isENTSOE,
    hasRealData: hasENTSOEData
  } = useENTSOEData(variable, region, Math.min(requestedDays, 7));
  const isLoading = isENTSOE ? entsoeLoading || loading : loading;
  const dataError = isENTSOE && entsoeError && !hasENTSOEData ? null : error; // Don't show ENTSOE error if we fall back to mock
  // Transform forecast data into chart format with per-model variations
  const data = useMemo(() => {
    // If we have real ENTSOE data, use it
    if (isENTSOE && hasENTSOEData && entsoeData.length > 0) {
      const days = requestedDays;
      const targetPoints = Math.min(entsoeData.length, days * 24);
      const selectedPoints = entsoeData.slice(0, targetPoints);
      // Downsample for long periods
      let displayPoints = selectedPoints;
      if (days >= 20) {
        const step = days >= 60 ? 12 : 4;
        displayPoints = selectedPoints.filter((_, i) => i % step === 0);
      } else if (days >= 7) {
        const step = 2;
        displayPoints = selectedPoints.filter((_, i) => i % step === 0);
      }
      // Create seeded randoms per entry for model variation
      const entryRands = entries.map(entry => {
        const seed = entry.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) * 137;
        let s = seed;
        return () => {
          s = (s * 16807 + 0) % 2147483647;
          return (s - 1) / 2147483646;
        };
      });
      return displayPoints.map((point, i) => {
        const date = new Date(point.dt * 1000);
        let timeLabel: string;
        if (days <= 1) {
          timeLabel = date.toLocaleString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        } else if (days <= 5) {
          timeLabel = date.toLocaleString('en-US', {
            weekday: 'short',
            hour: '2-digit',
            hour12: false
          });
        } else {
          timeLabel = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric'
          });
        }
        const dataEntry: Record<string, any> = {
          time: i,
          timeLabel
        };
        entries.forEach((entry, mi) => {
          // First entry gets real data, others get slight variations to simulate model differences
          const variation = mi === 0 ? 0 : (entryRands[mi]() - 0.5) * Math.abs(point.value) * 0.08;
          dataEntry[entry.id] = +(point.value + variation).toFixed(2);
        });
        return dataEntry;
      });
    }
    // Fall back to weather-derived mock data
    if (!forecastData.length) return [];
    const days = requestedDays;
    const targetPoints = days * 8;
    const availablePoints = Math.min(targetPoints, forecastData.length);
    // Create a seeded random per entry (using entry.id for unique seeds)
    const entryRands = entries.map(entry => {
      const seed = entry.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) * 137;
      return seededRandom(seed);
    });
    // Build data from real forecast points
    const result = forecastData.slice(0, availablePoints).map((point, i) => {
      const realValue = getVariableValue(point, variable);
      const date = new Date(point.dt * 1000);
      let timeLabel: string;
      if (days <= 1) {
        timeLabel = date.toLocaleString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      } else if (days <= 5) {
        timeLabel = date.toLocaleString('en-US', {
          weekday: 'short',
          hour: '2-digit',
          hour12: false
        });
      } else {
        timeLabel = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
      const dataEntry: Record<string, any> = {
        time: i,
        timeLabel
      };
      entries.forEach((entry, mi) => {
        const variation = (entryRands[mi]() - 0.5) * Math.abs(realValue) * 0.15;
        dataEntry[entry.id] = +(realValue + variation).toFixed(2);
      });
      return dataEntry;
    });
    // Extend beyond API data for longer timeframes
    if (targetPoints > availablePoints && availablePoints > 0) {
      const lastReal = forecastData[availablePoints - 1];
      const lastDate = new Date(lastReal.dt * 1000);
      const avgValue = forecastData.reduce((sum, p) => sum + getVariableValue(p, variable), 0) / forecastData.length;
      for (let i = availablePoints; i < targetPoints; i++) {
        const extraHours = (i - availablePoints + 1) * 3;
        const date = new Date(lastDate.getTime() + extraHours * 3600 * 1000);
        const dayOfYear = Math.floor((date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000);
        const seasonal = Math.sin(dayOfYear / 365 * Math.PI * 2) * Math.abs(avgValue) * 0.1;
        const timeLabel = date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        const dataEntry: Record<string, any> = {
          time: i,
          timeLabel
        };
        entries.forEach((entry, mi) => {
          const baseValue = avgValue + seasonal + (entryRands[mi]() - 0.5) * Math.abs(avgValue) * 0.2;
          dataEntry[entry.id] = +baseValue.toFixed(2);
        });
        result.push(dataEntry);
      }
      if (days >= 20) {
        const step = days >= 60 ? 12 : 4;
        return result.filter((_, i) => i % step === 0).map((p, i) => ({
          ...p,
          time: i
        }));
      }
    }
    return result;
  }, [forecastData, timePeriod, entries, variable]);
  // Compute Y-axis domain from data
  const yDomain = useMemo(() => {
    if (!data.length) return [0, 14];
    const allVals = data.flatMap(d => entries.map(e => typeof d[e.id] === 'number' ? d[e.id] : 0));
    const min = Math.floor(Math.min(...allVals) - 1);
    const max = Math.ceil(Math.max(...allVals) + 1);
    return [Math.max(0, min), max];
  }, [data, entries]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const isInMenu = (target as HTMLElement).closest('[data-portal-menu]');
      // Close 3-dot menu
      if (menuRef.current && !menuRef.current.contains(target) && buttonRef.current && !buttonRef.current.contains(target)) {
        setMenuOpen(false);
      }
      // Close Region menu
      if (regionOpen && regionButtonRef.current && !regionButtonRef.current.contains(target)) {
        if (!isInMenu) setRegionOpen(false);
      }
      // Close Time menu
      if (timeOpen && timeButtonRef.current && !timeButtonRef.current.contains(target)) {
        if (!isInMenu) setTimeOpen(false);
      }
      // Close Model menu
      if (modelOpen && modelButtonRef.current && !modelButtonRef.current.contains(target)) {
        if (!isInMenu) {
          setModelOpen(false);
          setModelSearch('');
        }
      }
      // Close Variable menu
      if (variableOpen && variableButtonRef.current && !variableButtonRef.current.contains(target)) {
        if (!isInMenu) setVariableOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [regionOpen, timeOpen, modelOpen, variableOpen]);
  const glassStyle: React.CSSProperties = {
    background: 'rgba(11, 17, 32, 0.80)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const dropdownStyle: React.CSSProperties = {
    zIndex: 9999,
    background: 'rgba(15, 23, 42, 0.95)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  // Auto-generate title from region + variable
  const autoTitle = `${region} - ${variable}`;
  // Derive line stroke dash from settings
  const strokeDash = settings.lineStyle === 'dashed' ? '8 4' : settings.lineStyle === 'dotted' ? '2 2' : undefined;
  // Compact mode: collapse controls when widget is narrow
  const controlsBarRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [compactMenuOpen, setCompactMenuOpen] = useState(false);
  const compactMenuRef = useRef<HTMLDivElement>(null);
  const compactButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = controlsBarRef.current;
    if (!el) return;
    const measure = (width: number) => {
      setIsCompact(width < 540);
    };
    // Immediate measurement
    measure(el.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      measure(entry.contentRect.width);
    });
    observer.observe(el);
    // Re-check after layout settles (react-grid-layout animates in)
    const t = setTimeout(() => {
      measure(el.getBoundingClientRect().width);
    }, 300);
    return () => {
      observer.disconnect();
      clearTimeout(t);
    };
  }, []);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (compactMenuOpen && compactMenuRef.current && !compactMenuRef.current.contains(e.target as Node) && compactButtonRef.current && !compactButtonRef.current.contains(e.target as Node)) {
        setCompactMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [compactMenuOpen]);
  return <WidgetWrapper ref={ref} title={autoTitle} onRemove={onRemove} onDoubleClickHandle={onDoubleClickHandle} style={style} className={className} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onTouchEnd={onTouchEnd} {...props} data-id="element-1212">
        <div className="jua-chart-inner relative flex flex-col h-full jua-widget-body" data-id="element-1213">
          {/* Controls Bar */}
          <div ref={controlsBarRef} className="relative z-50 flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] gap-2 min-w-0 overflow-visible" style={glassStyle} data-id="element-1214">
            <div className="flex items-center gap-3 min-w-0 overflow-hidden" data-id="element-1215">
              {/* Dynamic Legend */}
              {settings.showLegend && <div className="flex items-center gap-3 text-xs flex-shrink overflow-hidden" data-id="element-1216">
                  {entries.map(entry => {
              const isHidden = hiddenModels.has(entry.id);
              const sameModelCount = entries.filter(e => e.modelName === entry.modelName).length;
              const displayName = sameModelCount > 1 && entry.runIso ? `${entry.modelName} (${formatRunShort(entry.runIso)})` : entry.modelName;
              return <button key={entry.id} onClick={() => {
                setHiddenModels(prev => {
                  const next = new Set(prev);
                  if (next.has(entry.id)) {
                    next.delete(entry.id);
                  } else {
                    if (next.size < entries.length - 1) {
                      next.add(entry.id);
                    }
                  }
                  return next;
                });
              }} className={`flex items-center gap-1.5 flex-shrink-0 px-1.5 py-0.5 rounded transition-all ${isHidden ? 'opacity-35 hover:opacity-60' : 'opacity-100 hover:bg-white/[0.06]'}`} title={isHidden ? `Show ${displayName}` : `Hide ${displayName}`} data-id="element-1217">
                        <div className="w-3 h-[3px] rounded-full transition-colors" style={{
                  backgroundColor: isHidden ? '#475569' : getModelHexColor(entry.modelName, entries.indexOf(entry))
                }} data-id="element-1218" />
                        <span className={`font-medium whitespace-nowrap transition-colors ${isHidden ? 'text-white/30 line-through decoration-white/20' : 'text-white/80'}`} data-id="element-1219">
                          {displayName}
                        </span>
                      </button>;
            })}
                </div>}
            </div>

            {/* Dropdowns + Menu */}
            <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1220">
              {/* Compact consolidated dropdown */}
              {isCompact && <div className="relative" ref={compactMenuRef} data-id="element-1221">
                  <button ref={compactButtonRef} onClick={() => {
              closeAllDropdowns();
              setCompactMenuOpen(!compactMenuOpen);
            }} className="h-7 px-2 rounded-md text-[11px] font-medium text-white/90 border border-white/[0.1] bg-white/[0.06] hover:bg-white/[0.1] flex items-center gap-1.5 transition-colors" data-id="element-1222">
                    <SlidersHorizontal size={10} className="text-white/50 flex-shrink-0" data-id="element-1223" />
                    <span className="truncate max-w-[140px]" data-id="element-1224">{variable}</span>
                    <span className="text-white/30" data-id="element-1225">·</span>
                    <span className="text-white/60 truncate max-w-[80px]" data-id="element-1226">
                      {region}
                    </span>
                    <span className="text-white/30" data-id="element-1227">·</span>
                    <span className="text-white/60" data-id="element-1228">{timePeriod}</span>
                    <ChevronDown size={10} className="text-white/50 flex-shrink-0" data-id="element-1229" />
                  </button>

                  {compactMenuOpen && <div data-portal-menu className="absolute top-full right-0 mt-1 w-56 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-visible py-1" style={dropdownStyle} data-id="element-1230">
                      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
              }} data-id="element-1231" />
                      {/* Models row */}
                      <div className="relative" data-id="element-1232">
                        <button ref={modelButtonRef} onClick={() => {
                  setCompactMenuOpen(false);
                  closeAllDropdowns();
                  setModelOpen(true);
                }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1233">
                          <span className="flex items-center gap-2 text-white/40 uppercase tracking-wider text-[9px] font-semibold" data-id="element-1234">
                            <Cpu size={10} data-id="element-1235" /> Models
                          </span>
                          <span className="text-white/70 text-[11px]" data-id="element-1236">
                            {new Set(entries.map(e => e.modelName)).size}{' '}
                            selected
                          </span>
                        </button>
                        {modelOpen && <TableModelDropdown allModels={ALL_MODELS} selectedModelRuns={selectedModelRuns} onToggleModel={toggleModel} onToggleRun={handleToggleRun} onClearAll={handleClearAll} availableRuns={availableRuns} availableHourlyRuns={availableHourlyRuns} style={{
                  ...dropdownStyle,
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px'
                }} data-id="element-1237" />}
                      </div>
                      <div className="h-px bg-white/[0.06] mx-2" data-id="element-1238" />
                      {/* Variable row */}
                      <div className="relative" data-id="element-1239">
                        <button ref={variableButtonRef} onClick={() => {
                  setCompactMenuOpen(false);
                  closeAllDropdowns();
                  setVariableOpen(true);
                }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1240">
                          <span className="text-white/40 uppercase tracking-wider text-[9px] font-semibold" data-id="element-1241">
                            Variable
                          </span>
                          <span className="text-white/70 text-[11px] truncate max-w-[120px]" data-id="element-1242">
                            {variable}
                          </span>
                        </button>
                        {variableOpen && <div data-portal-menu className="absolute top-0 right-full mr-1 w-64 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden max-h-[min(420px,80vh)]" style={dropdownStyle} data-id="element-1243">
                            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
                  }} data-id="element-1244" />
                            <div className="relative max-h-[min(420px,80vh)] overflow-y-auto py-1" data-id="element-1245">
                              {/* Variable list — same as normal mode */}
                              {[{
                      label: 'Temperature',
                      items: ['Temp 2m', 'Dew Point 2m']
                    }, {
                      label: 'Wind',
                      items: ['Wind Speed 10m', 'Wind Dir 10m', 'Wind Speed 100m', 'Wind Dir 100m']
                    }, {
                      label: 'Precip / Radiation / Atmos',
                      items: ['Precipitation 1h', 'SSRD 1h', 'MSL Pressure']
                    }, {
                      label: 'Cloud Cover',
                      items: ['Cloud Cover High', 'Cloud Cover Medium', 'Cloud Cover Low']
                    }].map(({
                      label,
                      items
                    }) => <Fragment key={label} data-id="element-1246">
                                  <div className="relative px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider text-white/30" data-id="element-1247">
                                    {label}
                                  </div>
                                  {items.map(v => <button key={v} onClick={() => {
                        setVariable(v);
                        setVariableOpen(false);
                        onParameterChange?.();
                      }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1248">
                                      {v}
                                      {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1249" />}
                                    </button>)}
                                  <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1250" />
                                </Fragment>)}
                            </div>
                          </div>}
                      </div>
                      {/* Region row */}
                      <div className="relative" data-id="element-1251">
                        <button ref={regionButtonRef} onClick={() => {
                  setCompactMenuOpen(false);
                  closeAllDropdowns();
                  setRegionOpen(true);
                }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1252">
                          <span className="text-white/40 uppercase tracking-wider text-[9px] font-semibold" data-id="element-1253">
                            Region
                          </span>
                          <span className="text-white/70 text-[11px]" data-id="element-1254">
                            {region}
                          </span>
                        </button>
                        {regionOpen && <RegionSelector value={region} onChange={r => {
                  setRegion(r);
                  onParameterChange?.();
                }} onClose={() => setRegionOpen(false)} style={{
                  ...dropdownStyle,
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px'
                }} data-id="element-1255" />}
                      </div>
                      {/* Time Period row */}
                      <div className="relative" data-id="element-1256">
                        <button ref={timeButtonRef} onClick={() => {
                  setCompactMenuOpen(false);
                  closeAllDropdowns();
                  setTimeOpen(true);
                }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1257">
                          <span className="text-white/40 uppercase tracking-wider text-[9px] font-semibold" data-id="element-1258">
                            Period
                          </span>
                          <span className="text-white/70 text-[11px]" data-id="element-1259">
                            {timePeriod}
                          </span>
                        </button>
                        {timeOpen && <div data-portal-menu className="absolute top-full right-0 mt-1 w-32 rounded-xl shadow-2xl shadow-black/50 py-1 border border-white/[0.08] overflow-hidden" style={dropdownStyle} data-id="element-1260">
                            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
                  }} data-id="element-1261" />
                            {timePeriods.map(t => <button key={t} onClick={() => {
                    setTimePeriod(t);
                    setTimeOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1262">
                                {t}
                                {timePeriod === t && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1263" />}
                              </button>)}
                          </div>}
                      </div>
                    </div>}
                </div>}

              {/* Normal mode: individual dropdowns */}
              {!isCompact && <>
                  {/* Model Dropdown */}
                  <div className="relative" data-id="element-1264">
                    <button ref={modelButtonRef} onClick={() => {
                closeAllDropdowns();
                setModelOpen(!modelOpen);
              }} className="h-7 px-2 rounded-lg text-[11px] font-medium text-white/60 border border-white/[0.06] bg-transparent hover:bg-white/[0.04] hover:text-white/80 flex items-center gap-1.5 transition-all whitespace-nowrap" data-id="element-1265">
                      <Cpu size={10} className="text-white/35 flex-shrink-0" data-id="element-1266" />
                      <span className="truncate" data-id="element-1267">
                        {new Set(entries.map(e => e.modelName)).size} model
                        {new Set(entries.map(e => e.modelName)).size !== 1 ? 's' : ''}
                        {entries.length > new Set(entries.map(e => e.modelName)).size ? ` · ${entries.length} runs` : ''}
                      </span>
                      <ChevronDown size={10} className="text-white/30 flex-shrink-0" data-id="element-1268" />
                    </button>
                    {modelOpen && <TableModelDropdown allModels={ALL_MODELS} selectedModelRuns={selectedModelRuns} onToggleModel={toggleModel} onToggleRun={handleToggleRun} onClearAll={handleClearAll} availableRuns={availableRuns} availableHourlyRuns={availableHourlyRuns} style={{
                ...dropdownStyle,
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px'
              }} data-id="element-1269" />}
                  </div>

                  {/* Variable Dropdown */}
                  <div className="relative" data-id="element-1270">
                    <button ref={variableButtonRef} onClick={() => {
                closeAllDropdowns();
                setVariableOpen(!variableOpen);
              }} className="h-7 px-2 rounded-lg text-[11px] font-medium text-white/60 border border-white/[0.06] bg-transparent hover:bg-white/[0.04] hover:text-white/80 flex items-center gap-1.5 transition-all whitespace-nowrap max-w-[160px]" data-id="element-1271">
                      <span className="truncate" data-id="element-1272">{variable}</span>
                      <ChevronDown size={10} className="text-white/30 flex-shrink-0" data-id="element-1273" />
                    </button>
                    {variableOpen && <div data-portal-menu className="absolute top-full left-0 mt-1 w-64 rounded-xl shadow-2xl shadow-black/50 border border-white/[0.08] overflow-hidden max-h-[min(420px,80vh)]" style={dropdownStyle} data-id="element-1274">
                        <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
                }} data-id="element-1275" />
                        <div className="relative max-h-[min(420px,80vh)] overflow-y-auto py-1" data-id="element-1276">
                          <div className="relative px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-wider text-white/30" data-id="element-1277">
                            Temperature
                          </div>
                          {['Temp 2m', 'Dew Point 2m'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1278">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1279" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1280" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-white/30" data-id="element-1281">
                            Wind
                          </div>
                          {['Wind Speed 10m', 'Wind Dir 10m', 'Wind Speed 100m', 'Wind Dir 100m'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1282">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1283" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1284" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-slate-300" data-id="element-1285">
                            Precip / Radiation / Atmos
                          </div>
                          {['Precipitation 1h', 'SSRD 1h', 'MSL Pressure'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1286">
                                {v}
                                {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1287" />}
                              </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1288" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-white/30" data-id="element-1289">
                            Cloud Cover
                          </div>
                          {['Cloud Cover High', 'Cloud Cover Medium', 'Cloud Cover Low'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1290">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1291" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1292" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400/50" data-id="element-1293">
                            ENTSOE — Prices
                          </div>
                          {['Day-ahead Prices', 'Imbalance Prices'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1294">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1295" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1296" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400/50" data-id="element-1297">
                            ENTSOE — Generation
                          </div>
                          {['Generation (Actual)', 'Generation Forecast (Day-ahead)', 'Wind & Solar Forecast (Day-ahead)', 'Wind & Solar Forecast (Intraday)'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1298">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1299" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1300" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400/50" data-id="element-1301">
                            ENTSOE — Load
                          </div>
                          {['Load (Actual)', 'Load Forecast (Day-ahead)'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1302">
                                {v}
                                {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1303" />}
                              </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1304" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400/50" data-id="element-1305">
                            ENTSOE — Capacity &amp; Flows
                          </div>
                          {['Available Hydro Capacity', 'Cross-border Flows', 'NTC (Day-ahead)', 'NTC Weekahead', 'NTC Monthahead', 'NTC Yearahead'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1306">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1307" />}
                            </button>)}
                          <div className="mx-3 my-1 border-t border-white/[0.06]" data-id="element-1308" />
                          <div className="relative px-3 pt-1 pb-1 text-[9px] font-semibold uppercase tracking-wider text-amber-400/50" data-id="element-1309">
                            ENTSOE — Balancing
                          </div>
                          {['Imbalance Volumes', 'Net Position (Day-ahead)', 'Net Position (Total)', 'Scheduled Exchanges (Day-ahead)', 'Scheduled Exchanges (Total)'].map(v => <button key={v} onClick={() => {
                    setVariable(v);
                    setVariableOpen(false);
                    onParameterChange?.();
                  }} className="relative w-full px-3 py-1.5 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1310">
                              {v}
                              {variable === v && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1311" />}
                            </button>)}
                        </div>
                      </div>}
                  </div>

                  {/* Region Dropdown */}
                  <div className="relative" data-id="element-1312">
                    <button ref={regionButtonRef} onClick={() => {
                closeAllDropdowns();
                setRegionOpen(!regionOpen);
              }} className="h-7 px-2 rounded-lg text-[11px] font-medium text-white/60 border border-white/[0.06] bg-transparent hover:bg-white/[0.04] hover:text-white/80 flex items-center gap-1.5 transition-all whitespace-nowrap" data-id="element-1313">
                      <span className="truncate" data-id="element-1314">{region}</span>
                      <ChevronDown size={10} className="text-white/30 flex-shrink-0" data-id="element-1315" />
                    </button>
                    {regionOpen && <RegionSelector value={region} onChange={r => {
                setRegion(r);
                onParameterChange?.();
              }} onClose={() => setRegionOpen(false)} style={{
                ...dropdownStyle,
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px'
              }} data-id="element-1316" />}
                  </div>

                  {/* Time Period Dropdown */}
                  <div className="relative" data-id="element-1317">
                    <button ref={timeButtonRef} onClick={() => {
                closeAllDropdowns();
                setTimeOpen(!timeOpen);
              }} className="h-7 px-2 rounded-lg text-[11px] font-medium text-white/60 border border-white/[0.06] bg-transparent hover:bg-white/[0.04] hover:text-white/80 flex items-center gap-1.5 transition-all whitespace-nowrap" data-id="element-1318">
                      <span className="truncate" data-id="element-1319">{timePeriod}</span>
                      <ChevronDown size={10} className="text-white/30 flex-shrink-0" data-id="element-1320" />
                    </button>
                    {timeOpen && <div data-portal-menu className="absolute top-full left-0 mt-1 w-32 rounded-xl shadow-2xl shadow-black/50 py-1 border border-white/[0.08] overflow-hidden" style={dropdownStyle} data-id="element-1321">
                        <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
                }} data-id="element-1322" />
                        {timePeriods.map(t => <button key={t} onClick={() => {
                  setTimePeriod(t);
                  setTimeOpen(false);
                  onParameterChange?.();
                }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] hover:text-white flex items-center justify-between transition-colors" data-id="element-1323">
                            {t}
                            {timePeriod === t && <div className="w-1.5 h-1.5 rounded-full bg-[#EC4899] shadow-[0_0_6px_rgba(236,72,153,0.4)]" data-id="element-1324" />}
                          </button>)}
                      </div>}
                  </div>
                </>}

              {/* 3-dot menu — always visible */}
              <div className="relative" data-id="element-1325">
                <button ref={buttonRef} className="p-1.5 text-white/40 hover:text-white/80 transition-colors rounded-md hover:bg-white/[0.06]" onClick={() => {
              closeAllDropdowns();
              setMenuOpen(!menuOpen);
            }} data-id="element-1326">
                  <MoreVertical size={14} data-id="element-1327" />
                </button>
                {menuOpen && <div ref={menuRef} data-portal-menu className="absolute top-full right-0 mt-1 w-44 rounded-xl shadow-2xl shadow-black/50 py-1 border border-white/[0.08] overflow-hidden" style={dropdownStyle} data-id="element-1328">
                    <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
              }} data-id="element-1329" />
                    <button className="relative w-full px-3 py-2 text-left text-xs text-[#EC4899] hover:bg-[#EC4899]/10 flex items-center gap-2 font-medium" onClick={() => {
                setMenuOpen(false);
                setAiEditOpen(true);
              }} data-id="element-1330">
                      <Sparkles size={12} data-id="element-1331" /> Edit with AI
                    </button>
                    <button className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] flex items-center gap-2" onClick={() => {
                setSettingsOpen(true);
                setMenuOpen(false);
              }} data-id="element-1332">
                      <Settings2 size={12} data-id="element-1333" /> Display Settings
                    </button>
                    <div className="h-px bg-white/[0.08] my-1" data-id="element-1334" />
                    <button className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] flex items-center gap-2" data-id="element-1335">
                      <Copy size={12} data-id="element-1336" /> Duplicate
                    </button>
                    <button className="relative w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2" onClick={() => {
                onRemove?.();
                setMenuOpen(false);
              }} data-id="element-1337">
                      <Trash2 size={12} data-id="element-1338" /> Delete
                    </button>
                  </div>}
              </div>
            </div>
          </div>

          {/* Chart Area */}
          <div className="flex-1 min-h-0 p-3 pt-4 relative overflow-hidden" data-id="element-1339">
            {/* Contextual tip overlay */}
            {showTip && <div className="absolute top-2 left-3 right-3 z-20 transition-all duration-500 ease-out" style={{
          opacity: tipVisible ? 1 : 0,
          transform: tipVisible ? 'translateY(0)' : 'translateY(-4px)'
        }} data-id="element-1340">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#EC4899]/15 text-[11px] text-white/60" style={{
            background: 'rgba(236, 72, 153, 0.06)',
            backdropFilter: 'blur(12px)'
          }} data-id="element-1341">
                  <Lightbulb size={12} className="text-[#EC4899]/60 flex-shrink-0" data-id="element-1342" />
                  <span data-id="element-1343">
                    Switch regions and time periods with the dropdowns above ·
                    Use ⋮ for export options
                  </span>
                  <button onClick={() => {
              setTipVisible(false);
              setTimeout(() => setShowTip(false), 400);
            }} className="ml-auto p-0.5 text-white/30 hover:text-white/60 flex-shrink-0" data-id="element-1344">
                    <X size={10} data-id="element-1345" />
                  </button>
                </div>
              </div>}
            {isLoading ? <div className="h-full flex flex-col items-center justify-center gap-3" data-id="element-1346">
                <Loader2 size={24} className="text-[#EC4899] animate-spin" data-id="element-1347" />
                <span className="text-xs text-slate-400" data-id="element-1348">
                  Loading weather data...
                </span>
              </div> : dataError ? <div className="h-full flex flex-col items-center justify-center gap-3" data-id="element-1349">
                <CloudOff size={24} className="text-slate-500" data-id="element-1350" />
                <span className="text-xs text-slate-400" data-id="element-1351">
                  Failed to load data
                </span>
                <span className="text-[10px] text-slate-500" data-id="element-1352">{dataError}</span>
              </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-1353">
                <LineChart data={data} margin={{
            top: 5,
            right: 10,
            left: -10,
            bottom: 0
          }} data-id="element-1354">
                  {settings.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" vertical={true} data-id="element-1355" />}
                  <XAxis dataKey="time" stroke="#475569" tick={{
              fontSize: 10,
              fill: '#64748b'
            }} tickLine={false} axisLine={{
              stroke: 'rgba(148,163,184,0.15)'
            }} tickFormatter={val => {
              const point = data[val];
              return point?.timeLabel ?? '';
            }} interval={(() => {
              const daysMatch = timePeriod.match(/(\d+)/);
              const days = daysMatch ? parseInt(daysMatch[1]) : 7;
              if (days <= 1) return 0;
              if (days <= 3) return Math.max(1, Math.floor(data.length / 8));
              if (days <= 5) return Math.max(1, Math.floor(data.length / 7));
              if (days <= 7) return Math.max(1, Math.floor(data.length / 7));
              if (days <= 20) return Math.max(1, Math.floor(data.length / 8));
              return Math.max(1, Math.floor(data.length / 8)); // 60 days
            })()} label={{
              value: `${cityName || region}`,
              position: 'insideBottom',
              offset: -2,
              style: {
                fill: '#64748b',
                fontSize: 10
              }
            }} data-id="element-1356" />
                  <YAxis stroke="#475569" tick={{
              fontSize: 10,
              fill: '#64748b'
            }} tickLine={false} axisLine={false} domain={yDomain} label={{
              value: `${variable} (${getVariableUnit(variable)})`,
              angle: -90,
              position: 'insideLeft',
              offset: 15,
              style: {
                textAnchor: 'middle',
                fill: '#64748b',
                fontSize: 10
              }
            }} data-id="element-1357" />
                  <Tooltip content={({
              active,
              payload,
              label
            }) => {
              if (!active || !payload?.length) return null;
              const unit = getVariableUnit(variable);
              const timeLabel = data[label]?.timeLabel ?? `Point ${label}`;
              return <div className="rounded-xl px-4 py-3 border border-white/[0.1] shadow-2xl shadow-black/50 min-w-[180px]" style={{
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(20px)'
              }} data-id="element-1359">
                          <div className="text-[11px] text-slate-400 mb-2.5 font-medium" data-id="element-1360">
                            {timeLabel}
                          </div>
                          <div className="space-y-1.5" data-id="element-1361">
                            {payload.map((item: any) => {
                    const entry = entries.find(e => e.id === item.dataKey);
                    const color = entry ? getModelHexColor(entry.modelName, entries.indexOf(entry)) : item.color;
                    const sameModelCount = entry ? entries.filter(e => e.modelName === entry.modelName).length : 1;
                    const displayName = entry && sameModelCount > 1 && entry.runIso ? `${entry.modelName} (${formatRunShort(entry.runIso)})` : entry?.modelName || item.name;
                    return <div key={item.dataKey} className="flex items-center justify-between gap-4" data-id="element-1362">
                                  <div className="flex items-center gap-2" data-id="element-1363">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                          backgroundColor: color
                        }} data-id="element-1364" />
                                    <span className="text-[11px] text-slate-300 font-medium" data-id="element-1365">
                                      {displayName}
                                    </span>
                                  </div>
                                  <div className="flex items-baseline gap-1" data-id="element-1366">
                                    <span className="text-[12px] text-white font-semibold tabular-nums" data-id="element-1367">
                                      {typeof item.value === 'number' ? item.value.toFixed(2) : item.value}
                                    </span>
                                    <span className="text-[10px] text-slate-500" data-id="element-1368">
                                      {unit}
                                    </span>
                                  </div>
                                </div>;
                  })}
                          </div>
                        </div>;
            }} data-id="element-1358" />
                  {entries.map((entry, i) => {
              if (hiddenModels.has(entry.id)) return null;
              const color = getModelHexColor(entry.modelName, i);
              // Different runs of same model get dashed lines
              const sameModelEntries = entries.filter(e => e.modelName === entry.modelName);
              const runIndex = sameModelEntries.indexOf(entry);
              const runDash = runIndex === 0 ? strokeDash : runIndex === 1 ? '6 3' : '3 3';
              const sameModelCount = sameModelEntries.length;
              const displayName = sameModelCount > 1 && entry.runIso ? `${entry.modelName} (${formatRunShort(entry.runIso)})` : entry.runIso ? `${entry.modelName} (${formatRunShort(entry.runIso)})` : entry.modelName;
              return <Line key={entry.id} type="monotone" dataKey={entry.id} name={displayName} stroke={color} strokeWidth={2.5} strokeDasharray={sameModelCount > 1 ? runDash : strokeDash} dot={false} activeDot={{
                r: 4,
                fill: color,
                stroke: '#fff',
                strokeWidth: 2
              }} data-id="element-1369" />;
            })}
                </LineChart>
              </ResponsiveContainer>}
          </div>

          {/* Bottom Info Bar */}
          <div className="px-4 py-2 flex items-center justify-between text-xs border-t border-white/[0.06]" style={glassStyle} data-id="element-1370">
            <div className="flex items-center gap-2 text-white/50" data-id="element-1371">
              <Database size={12} data-id="element-1372" />
              <span className="font-medium text-white/70" data-id="element-1373">
                {cityName || region} - {variable}
              </span>
              <span className="text-white/20" data-id="element-1374">|</span>
              {locationOverride ? <span className="text-amber-400/80 flex items-center gap-1" data-id="element-1375">
                  <Lock size={10} data-id="element-1376" /> Location Override
                </span> : modelOverride ? <span className="text-amber-400/80 flex items-center gap-1" data-id="element-1377">
                  <Lock size={10} data-id="element-1378" /> Global Override
                </span> : isENTSOE && hasENTSOEData ? <span className="text-amber-400/70 flex items-center gap-1" data-id="element-1379">
                  ⚡ ENTSOE Live
                </span> : isENTSOE && entsoeError ? <span className="text-orange-400/70 flex items-center gap-1" title={entsoeError} data-id="element-1380">
                  ⚠ ENTSOE fallback (mock)
                </span> : <span className="text-emerald-400/70" data-id="element-1381">OpenWeatherMap Live</span>}
            </div>
            <Info size={12} className="text-white/30" data-id="element-1382" />
          </div>

          {/* Settings Panel Portal */}
          <ChartSettingsPanel open={settingsOpen} onOpenChange={setSettingsOpen} settings={settings} onSettingsChange={setSettings} region={region} data-id="element-1383" />

          {/* AI Edit Panel */}
          {aiEditOpen && <ChartAiEditPanel aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} aiResult={aiResult} aiLoading={aiLoading} onSubmit={handleAiEdit} onClose={() => {
        setAiEditOpen(false);
        setAiResult('');
        setAiPrompt('');
      }} data-id="element-1384" />}
        </div>
      </WidgetWrapper>;
});